import type {
  AlimentoMacronutriente,
  EscopoJornada,
  TurnoReferencia,
  ValorReferenciaRefeicao,
} from '../types/database'
import { REFEICOES, TURNOS_REFERENCIA } from '../types/database'

export type RefeicaoFicha = (typeof REFEICOES)[number]['value']

export type NutrientesTotais = {
  energia_kcal: number
  energia_kj: number
  carboidratos_g: number
  proteinas_g: number
  lipideos_g: number
  vitamina_a_mcg: number
  vitamina_c_g: number
  calcio_mg: number
  ferro_mg: number
}

export type ComparacaoStatus = 'ok' | 'abaixo' | 'acima' | 'sem_meta'

const EMPTY: NutrientesTotais = {
  energia_kcal: 0,
  energia_kj: 0,
  carboidratos_g: 0,
  proteinas_g: 0,
  lipideos_g: 0,
  vitamina_a_mcg: 0,
  vitamina_c_g: 0,
  calcio_mg: 0,
  ferro_mg: 0,
}

const PACOTE_REFEICOES: Record<TurnoReferencia, RefeicaoFicha[]> = {
  manha: ['lanche_manha', 'almoco'],
  tarde: ['lanche_tarde', 'jantar'],
  noite: ['jantar'],
  integral: ['lanche_manha', 'almoco', 'lanche_tarde', 'jantar'],
}

export function refeicoesDoTurno(turno: TurnoReferencia): RefeicaoFicha[] {
  return [...PACOTE_REFEICOES[turno]]
}

/**
 * Escopo de gravação/leitura do cardápio e ficha.
 * Manhã/Tarde/Noite → sempre parcial.
 * Integral → integral só se a modalidade tiver flag independente; senão parcial (compartilhado).
 */
export function escopoJornadaDoTurno(
  turno: TurnoReferencia,
  integralIndependente: boolean,
): EscopoJornada {
  if (turno === 'integral' && integralIndependente) return 'integral'
  return 'parcial'
}

export function labelRefeicao(refeicao: string): string {
  return REFEICOES.find((r) => r.value === refeicao)?.label || refeicao
}

export function labelTurnoPacote(turno: TurnoReferencia): { label: string; pacote: string } {
  const t = TURNOS_REFERENCIA.find((x) => x.value === turno)
  return { label: t?.label || turno, pacote: t?.pacote || '' }
}

/** Nutrientes da porção a partir do alimento (por 100 g) × quantidade (g). */
export function nutrientesDaPorcao(
  alimento: Pick<
    AlimentoMacronutriente,
    | 'energia_kcal'
    | 'energia_kj'
    | 'carboidratos_g'
    | 'proteinas_g'
    | 'lipideos_g'
    | 'vitamina_a_mcg'
    | 'vitamina_c_g'
    | 'calcio_mg'
    | 'ferro_mg'
  > | null
  | undefined,
  quantidadeG: number,
): NutrientesTotais {
  if (!alimento) return { ...EMPTY }
  const f = (Number(quantidadeG) || 0) / 100
  return {
    energia_kcal: (Number(alimento.energia_kcal) || 0) * f,
    energia_kj: (Number(alimento.energia_kj) || 0) * f,
    carboidratos_g: (Number(alimento.carboidratos_g) || 0) * f,
    proteinas_g: (Number(alimento.proteinas_g) || 0) * f,
    lipideos_g: (Number(alimento.lipideos_g) || 0) * f,
    vitamina_a_mcg: (Number(alimento.vitamina_a_mcg) || 0) * f,
    vitamina_c_g: (Number(alimento.vitamina_c_g) || 0) * f,
    calcio_mg: (Number(alimento.calcio_mg) || 0) * f,
    ferro_mg: (Number(alimento.ferro_mg) || 0) * f,
  }
}

export function somarNutrientes(list: NutrientesTotais[]): NutrientesTotais {
  return list.reduce(
    (acc, n) => ({
      energia_kcal: acc.energia_kcal + n.energia_kcal,
      energia_kj: acc.energia_kj + n.energia_kj,
      carboidratos_g: acc.carboidratos_g + n.carboidratos_g,
      proteinas_g: acc.proteinas_g + n.proteinas_g,
      lipideos_g: acc.lipideos_g + n.lipideos_g,
      vitamina_a_mcg: acc.vitamina_a_mcg + n.vitamina_a_mcg,
      vitamina_c_g: acc.vitamina_c_g + n.vitamina_c_g,
      calcio_mg: acc.calcio_mg + n.calcio_mg,
      ferro_mg: acc.ferro_mg + n.ferro_mg,
    }),
    { ...EMPTY },
  )
}

export function compararValorUnico(obtido: number, meta: number | null | undefined): ComparacaoStatus {
  if (meta == null || !Number.isFinite(Number(meta)) || Number(meta) <= 0) return 'sem_meta'
  const m = Number(meta)
  const tol = Math.max(m * 0.02, 0.01)
  if (obtido < m - tol) return 'abaixo'
  if (obtido > m + tol) return 'acima'
  return 'ok'
}

export function compararFaixa(
  obtido: number,
  min: number | null | undefined,
  max: number | null | undefined,
): ComparacaoStatus {
  const hasMin = min != null && Number.isFinite(Number(min)) && Number(min) > 0
  const hasMax = max != null && Number.isFinite(Number(max)) && Number(max) > 0
  if (!hasMin && !hasMax) return 'sem_meta'
  if (hasMin && obtido < Number(min)) return 'abaixo'
  if (hasMax && obtido > Number(max)) return 'acima'
  return 'ok'
}

export function compararComReferencia(
  total: NutrientesTotais,
  ref: ValorReferenciaRefeicao | null | undefined,
): Record<keyof NutrientesTotais, ComparacaoStatus> {
  return {
    energia_kcal: compararValorUnico(total.energia_kcal, ref?.energia_kcal),
    energia_kj: compararValorUnico(total.energia_kj, ref?.energia_kj),
    carboidratos_g: compararFaixa(total.carboidratos_g, ref?.carboidratos_g_min, ref?.carboidratos_g_max),
    proteinas_g: compararFaixa(total.proteinas_g, ref?.proteinas_g_min, ref?.proteinas_g_max),
    lipideos_g: compararFaixa(total.lipideos_g, ref?.lipideos_g_min, ref?.lipideos_g_max),
    vitamina_a_mcg: compararValorUnico(total.vitamina_a_mcg, ref?.vitamina_a_mcg),
    vitamina_c_g: compararValorUnico(total.vitamina_c_g, ref?.vitamina_c_g),
    calcio_mg: compararValorUnico(total.calcio_mg, ref?.calcio_mg),
    ferro_mg: compararValorUnico(total.ferro_mg, ref?.ferro_mg),
  }
}
